import React, { useContext } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Button,
  Divider,
  useTheme,
  Tooltip,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import SettingsIcon from "@mui/icons-material/Settings";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import HandshakeIcon from "@mui/icons-material/Handshake";
import TerminalIcon from "@mui/icons-material/Terminal";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CableIcon from "@mui/icons-material/Cable";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import logo from "../../../Assets/LOGO.png";
import favicon from "../../../Assets/favicon.png"; // Fallback small logo or avatar if exists, otherwise hide logo image
import { ColorModeContext } from "../../../App";
import { decryptData } from "../../../Core/util/obfuscate";

const menuItems = [
  { path: "/admin", title: "Admin", icon: AdminPanelSettingsIcon },
  { path: "/user", title: "Users", icon: PeopleIcon },
  { path: "/companies", title: "Companies", icon: BusinessIcon },
  { path: "/subscriptions", title: "Subscriptions", icon: ReceiptLongIcon },
  { path: "/logs", title: "Logs", icon: TerminalIcon },
  { path: "/partners", title: "Partners", icon: HandshakeIcon },
  { path: "/appconfig", title: "App Config", icon: SettingsIcon },
  { path: "/enquiry", title: "Enquiries", icon: QuestionAnswerIcon },
  { path: "/deletion-requests", title: "Deletion Requests", icon: DeleteSweepIcon },
  { path: "/connections", title: "Connections", icon: CableIcon },
];

const hasPermissionForPath = (path: string, adminMe: any): boolean => {
  if (!adminMe) return true; // Default fallback to visible
  if (adminMe.isSuperAdmin || adminMe.superAdmin) return true;

  const permissions = adminMe.permission || {};

  switch (path) {
    case "/admin":
      return !!(permissions.adminSettings?.manageAdmins?.read || permissions.adminSettings?.read);
    case "/user":
      return !!permissions.users?.read;
    case "/companies":
      return !!permissions.companies?.read;
    case "/subscriptions":
      return !!permissions.subscriptions?.read;
    case "/logs":
      return !!permissions.logs?.read;
    case "/partners":
      return !!permissions.partners?.read;
    case "/appconfig":
      return !!(permissions.adminSettings?.appConfig?.read || permissions.adminSettings?.read);
    case "/enquiry":
      return !!permissions.enquiries?.read;
    case "/deletion-requests":
      return !!(permissions.users?.deleteRequests?.read || permissions.users?.read);
    case "/connections":
      return !!permissions.socketConnections?.read;
    default:
      return true;
  }
};

interface SideBarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export default function SideBar({ isCollapsed, onToggleCollapse }: SideBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { toggleColorMode, mode } = useContext(ColorModeContext);
  const [adminMe, setAdminMe] = React.useState<any>(null);

  React.useEffect(() => {
    const cached = localStorage.getItem("adminMe");
    if (cached) {
      const decrypted = decryptData(cached);
      if (decrypted) {
        setAdminMe(decrypted);
      }
    }
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("name");
    localStorage.removeItem("phone");
    localStorage.removeItem("adminMe");
    navigate("/login");
  };

  const visibleMenuItems = menuItems.filter(item => hasPermissionForPath(item.path, adminMe));

  return (
    <Box
      sx={{
        width: isCollapsed ? "72px" : "280px",
        minWidth: isCollapsed ? "72px" : "280px",
        maxWidth: isCollapsed ? "72px" : "280px",
        flexShrink: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#1e293b",
        borderRight: `1px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.25s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
      }}
    >
      {/* Brand Logo Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "space-between",
          paddingX: isCollapsed ? "8px" : "16px",
          height: "70px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        {!isCollapsed ? (
          <>
            <img
              src={logo}
              alt="Accosync logo"
              style={{
                width: "140px",
                height: "40px",
                objectFit: "contain",
                filter: theme.palette.mode === "light" ? "brightness(0)" : "none",
              }}
            />
            <IconButton onClick={onToggleCollapse} size="small" sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <ChevronLeftIcon />
            </IconButton>
          </>
        ) : (
          <IconButton onClick={onToggleCollapse} size="small" sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation List */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          paddingX: "8px",
          paddingY: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.mode === "light" ? "#cbd5e1" : "#475569",
            borderRadius: "4px",
          },
        }}
      >
        <List sx={{ display: "flex", flexDirection: "column", gap: "6px", padding: 0 }}>
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path ||
              (item.path === "/user" && location.pathname === "/");

            const itemButton = (
              <ListItemButton
                component={NavLink}
                to={item.path}
                sx={{
                  borderRadius: "8px",
                  paddingY: "10px",
                  paddingX: isCollapsed ? "0" : "16px",
                  justifyContent: isCollapsed ? "center" : "flex-start",
                  minHeight: "44px",
                  backgroundColor: isActive
                    ? theme.palette.mode === "light"
                      ? "#f0f7ff"
                      : "#1e3a5f"
                    : "transparent",
                  color: isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  borderLeft: !isCollapsed && isActive
                    ? `4px solid ${theme.palette.primary.main}`
                    : "4px solid transparent",
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "light" ? "#f1f5f9" : "#334155",
                    color: theme.palette.text.primary,
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isCollapsed ? "auto" : "40px",
                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    justifyContent: "center",
                  }}
                >
                  <Icon fontSize="medium" />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      fontWeight: isActive ? 600 : 500,
                      noWrap: true,
                    }}
                  />
                )}
              </ListItemButton>
            );

            return isCollapsed ? (
              <Tooltip key={item.path} title={item.title} placement="right" arrow>
                {itemButton}
              </Tooltip>
            ) : (
              <React.Fragment key={item.path}>
                {itemButton}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      <Divider />

      {/* Footer Area with Theme Toggle and Logout */}
      <Box
        sx={{
          padding: isCollapsed ? "12px 8px" : "16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {/* Theme toggle row */}
        {isCollapsed ? (
          <Tooltip title={mode === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"} placement="right" arrow>
            <IconButton onClick={toggleColorMode} color="primary" sx={{ border: `1px solid ${theme.palette.divider}` }}>
              {mode === "light" ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              paddingX: "8px",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 550, color: "text.secondary" }}>
              {mode === "light" ? "Light Mode" : "Dark Mode"}
            </Typography>
            <IconButton onClick={toggleColorMode} color="primary" sx={{ border: `1px solid ${theme.palette.divider}` }}>
              {mode === "light" ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            </IconButton>
          </Box>
        )}

        {/* Logout Button */}
        {isCollapsed ? (
          <Tooltip title="Logout" placement="right" arrow>
            <IconButton
              color="error"
              onClick={handleLogout}
              sx={{
                border: `1px solid ${theme.palette.mode === "light" ? "#fecaca" : "#7f1d1d"}`,
                backgroundColor: theme.palette.mode === "light" ? "#fef2f2" : "#450a0a",
                "&:hover": {
                  backgroundColor: theme.palette.mode === "light" ? "#fee2e2" : "#7f1d1d",
                },
              }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            color="error"
            fullWidth
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              justifyContent: "center",
              paddingY: "8px",
              fontSize: "14px",
              borderColor: theme.palette.mode === "light" ? "#fecaca" : "#7f1d1d",
              "&:hover": {
                backgroundColor: theme.palette.mode === "light" ? "#fef2f2" : "#450a0a",
              },
            }}
          >
            Logout
          </Button>
        )}
      </Box>
    </Box>
  );
}
